import { createContext, useContext, useState, useEffect } from 'react'
import { authApi } from '../api/client'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchUser()
  }, [])

  const fetchUser = async () => {
    try {
      // First check if user is authenticated via Azure SWA Easy Auth
      const swaAuthResponse = await fetch('/.auth/me')
      const swaAuthData = await swaAuthResponse.json()
      
      if (swaAuthData.clientPrincipal) {
        // User is authenticated with Azure AD, now get/create user in our backend
        try {
          const response = await authApi.getMe()
          setUser(response.data)
        } catch (backendError) {
          // Backend might not have the user yet, but they're authenticated
          // Set basic user info from SWA auth
          console.warn('Backend user fetch failed, using SWA auth data:', backendError)
          setUser({
            email: swaAuthData.clientPrincipal.userDetails,
            display_name: swaAuthData.clientPrincipal.userDetails,
            role: 'member',
            azure_ad_oid: swaAuthData.clientPrincipal.userId,
          })
        }
      } else {
        setUser(null)
      }
    } catch (error) {
      console.error('Failed to fetch user:', error)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  const login = () => {
    // Redirect to Azure AD login via Easy Auth
    window.location.href = '/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(window.location.pathname)
  }

  const logout = () => {
    window.location.href = '/.auth/logout?post_logout_redirect_uri=' + encodeURIComponent('/')
  }

  const refreshUser = async () => {
    await fetchUser()
  }

  const updateUser = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : null)
  }

  const value = {
    user,
    loading,
    login,
    logout,
    refreshUser,
    updateUser,
    isAdmin: user?.role === 'admin',
    isManager: user?.role === 'admin' || user?.role === 'manager',
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuthContext() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}
